import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useEffect } from 'react';

export interface PractitionerNotification {
  id: string;
  practitioner_id: string;
  type: 'credit_alert' | 'milestone_achievement' | 'monthly_reset' | 'system_update';
  title: string;
  message: string;
  is_read: boolean;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export const usePractitionerNotifications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch notifications
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['practitioner-notifications', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await backendClient
        .from('practitioner_notifications')
        .select('*')
        .eq('practitioner_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as PractitionerNotification[];
    },
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // Mark notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await backendClient
        .from('practitioner_notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('practitioner_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practitioner-notifications', user?.id] });
    },
    onError: (error) => {
      console.error('Failed to mark notification as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        variant: "destructive",
      });
    },
  });

  // Mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const { error } = await backendClient
        .from('practitioner_notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('practitioner_id', user?.id)
        .eq('is_read', false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practitioner-notifications', user?.id] });
      toast({
        title: "Success",
        description: "All notifications marked as read",
      });
    },
    onError: (error) => {
      console.error('Failed to mark all notifications as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark all notifications as read",
        variant: "destructive",
      });
    },
  });

  // Delete notification
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await backendClient
        .from('practitioner_notifications')
        .delete()
        .eq('id', notificationId)
        .eq('practitioner_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practitioner-notifications', user?.id] });
    },
    onError: (error) => {
      console.error('Failed to delete notification:', error);
      toast({
        title: "Error",
        description: "Failed to delete notification",
        variant: "destructive",
      });
    },
  });

  // Set up real-time subscription (only when using Supabase, not backend)
  useEffect(() => {
    if (!user) return;

    // Skip realtime subscriptions when using backend API
    const useBackend = import.meta.env.VITE_USE_BACKEND === 'true';
    if (useBackend) {
      console.log('Using backend API - skipping Supabase realtime subscription for notifications');
      return;
    }

    try {
      const channel = supabase
        .channel('practitioner-notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'practitioner_notifications',
            filter: `practitioner_id=eq.${user.id}`,
          },
          (payload) => {
            const newNotification = payload.new as PractitionerNotification;

            // Show toast for new notifications
            toast({
              title: newNotification.title,
              description: newNotification.message,
              duration: 5000,
            });

            // Invalidate queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['practitioner-notifications', user.id] });
          }
        )
        .subscribe();

      return () => {
        backendClient.removeChannel(channel);
      };
    } catch (error) {
      console.error('Failed to set up realtime subscription:', error);
      // Don't throw - just log the error and continue without realtime updates
    }
  }, [user, toast, queryClient]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteNotification: deleteNotificationMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isDeletingNotification: deleteNotificationMutation.isPending,
    refetch,
  };
};
